import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, AlertTriangle, Copy, CheckCircle2, Loader2, XCircle, ChevronDown, ChevronRight, Ban, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";
import StudySetupPanels from "./StudySetupPanels";
import StudyFeesTable from "./StudyFeesTable";
import AboutStudyPanel from "./AboutStudyPanel";
import PatientTotalCheck from "./PatientTotalCheck";
import { ProcBilling, ProcBillingNote, OverheadRow, AdditionalProcedureRows, StudyAdditionalProcedures } from "./ProcBillingChips";

const SEV = {
    high: "text-rose-300 border-rose-400/40 bg-rose-400/10",
    medium: "text-amber-300 border-amber-400/40 bg-amber-400/10",
    low: "text-sky-300 border-sky-400/40 bg-sky-400/10",
    info: "text-white/60 border-white/15 bg-white/5",
};
const STATUS = {
    queued: ["text-white/60 border-white/20", Loader2, "Queued"],
    ingesting: ["text-sky-300 border-sky-400/40", Loader2, "Ingesting documents"],
    composing: ["text-violet-300 border-violet-400/40", Loader2, "Composing"],
    ready: ["text-emerald-300 border-emerald-400/40", CheckCircle2, "Ready"],
    failed: ["text-rose-300 border-rose-400/40", XCircle, "Failed"],
    cancelled: ["text-white/45 border-white/15", Ban, "Cancelled"],
};
export const TERMINAL = ["ready", "failed", "cancelled"];

export function StatusChip({ status, testId }) {
    const [cls, Icon, label] = STATUS[status] || STATUS.queued;
    const spin = !TERMINAL.includes(status);
    return (
        <span data-testid={testId} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${cls}`}>
            <Icon className={`w-3 h-3 ${spin ? "animate-spin" : ""}`} /> {label}
        </span>
    );
}

// Cancel (active) / Retry (failed · cancelled) — shared by the detail header and the list rows.
export function BuildActions({ build, onDone, compact = false }) {
    const [busy, setBusy] = useState(null);
    if (!build) return null;
    const run = async (kind, path) => {
        if (kind === "cancel" && !window.confirm("Stop this study build? Documents already extracted are kept and reused by a retry.")) return;
        setBusy(kind);
        try {
            const { data } = await api.post(`/admin/study-assets/study-builds/${build.id}${path}`);
            toast.success(kind === "cancel" ? "Build cancelled" : "Build re-queued");
            onDone?.(data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(null); }
    };
    const btn = (kind, label, path, Icon, cls) => (
        <button key={kind} onClick={(e) => { e.stopPropagation(); run(kind, path); }} disabled={!!busy} data-testid={`sb-action-${kind}-${build.id}`}
            title={label}
            className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] disabled:opacity-50 ${cls}`}>
            {busy === kind ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}{compact ? null : <span>{label}</span>}
        </button>
    );
    if (!TERMINAL.includes(build.status)) {
        return <div className="flex items-center gap-1">{btn("cancel", "Cancel build", "/cancel", Ban, "border-rose-400/40 text-rose-200 hover:bg-rose-400/10")}</div>;
    }
    if (["failed", "cancelled"].includes(build.status)) {
        return (
            <div className="flex items-center gap-1">
                {btn("retry", "Retry", "/retry", RotateCcw, "border-[#7C3AED]/50 text-[#e2d4ff] hover:bg-[#7C3AED]/15")}
                {btn("retry-reextract", "Retry + re-extract", "/retry?force_reextract=true", RefreshCw, "border-white/15 text-white/70 hover:border-white/30")}
            </div>
        );
    }
    return null;
}

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Full view of one build — polls while it runs; shows discrepancies, the
// visit schedule with per-procedure matching, and the raw request / result JSON.
export default function StudyBuildDetail({ buildId, initial, onClose, onChanged }) {
    const [build, setBuild] = useState(initial || null);
    const [tab, setTab] = useState("overview");
    const active = build && !TERMINAL.includes(build.status);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get(`/admin/study-assets/study-builds/${buildId}`);
                if (alive) { setBuild(data); onChanged?.(data); }
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        };
        load();
        const t = setInterval(() => { if (alive) load(); }, 4000);
        return () => { alive = false; clearInterval(t); };
    }, [buildId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const b = build?.build || {};
    const discrepancies = build?.discrepancies || b.discrepancies || [];
    const copy = async (obj, label) => { try { await navigator.clipboard.writeText(JSON.stringify(obj, null, 2)); toast.success(`${label} copied`); } catch { toast.error("Copy was blocked by the browser"); } };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-auto p-6" onMouseDown={(e) => e.stopPropagation()} data-testid="study-build-detail">
                <div className="flex items-start justify-between mb-4 gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h2 className="font-display text-2xl" data-testid="sb-detail-title">{b.study?.protocol_number || build?.summary?.protocol_number || (build?.id || buildId).slice(0, 8)}</h2>
                            {build && <StatusChip status={build.status} testId="sb-detail-status" />}
                            {build?.cost && (
                                <span className="text-[11px] px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-200 tabular-nums" data-testid="sb-detail-cost" title={`${build.cost.call_detail} · ${build.cost.call_timestamp} UTC — LLM spend + reuse charges for this build`}>
                                    AI cost ${Number(build.cost.total_cost).toFixed(4)}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-white/50 mt-1">{[b.study?.title || build?.summary?.title, b.study?.sponsor, b.study?.version, b.study?.site].filter(Boolean).join(" · ")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <BuildActions build={build} onDone={(d) => { setBuild(d); onChanged?.(d); }} />
                        <button onClick={onClose} data-testid="sb-detail-close"><X className="w-5 h-5 text-white/50" /></button>
                    </div>
                </div>

                {build && active && <Progress build={build} />}
                {build?.status === "failed" && (
                    <div className="rounded-xl border border-rose-400/30 bg-rose-400/5 p-3 mb-4 text-sm text-rose-200" data-testid="sb-detail-error">
                        <div className="flex items-center gap-2 font-medium"><XCircle className="w-4 h-4" /> Build failed</div>
                        <div className="mt-1 text-xs">{build.error}</div>
                        <Steps steps={build.steps} />
                    </div>
                )}
                {build?.status === "cancelled" && (
                    <div className="rounded-xl border border-white/15 bg-white/[0.03] p-3 mb-4 text-sm text-white/70" data-testid="sb-detail-cancelled">
                        <div className="flex items-center gap-2 font-medium"><Ban className="w-4 h-4" /> Build cancelled</div>
                        <div className="mt-1 text-xs">{build.error}{build.retries?.length ? ` · ${build.retries.length} earlier retr${build.retries.length === 1 ? "y" : "ies"}` : ""}</div>
                        <Steps steps={build.steps} />
                    </div>
                )}

                <div className="flex items-center gap-1 mb-4 flex-wrap" data-testid="sb-detail-tabs">
                    {[["overview", "Overview"], ["discrepancies", `Discrepancies${build ? ` (${discrepancies.length})` : ""}`], ["setup", "Study setup"], ["visits", `Visits${build ? ` (${(b.visits || []).length})` : ""}`],
                      ["fees", `Study fees${b.study_fees ? ` (${b.study_fees.length})` : ""}`], ["about", "About Study"], ["request", "Request JSON"], ["result", "Result JSON"], ["api", "API call"]].map(([k, label]) => (
                        <button key={k} onClick={() => setTab(k)} data-testid={`sb-tab-${k}`}
                            className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded border ${tab === k ? "border-[#7C3AED]/60 bg-[#7C3AED]/15 text-[#e2d4ff]" : "border-white/10 text-white/55 hover:border-white/20"}`}>{label}</button>
                    ))}
                </div>

                {tab === "overview" && <Overview build={build} b={b} discrepancies={discrepancies} />}
                {tab === "discrepancies" && <Discrepancies items={discrepancies} />}
                {tab === "setup" && <StudySetupPanels b={b} />}
                {tab === "visits" && <Visits visits={b.visits || []} studyExtra={b.study_level_additional_procedures} />}
                {tab === "fees" && <StudyFeesTable fees={b.study_fees} leftOut={b.setup_info?.fees_left_out} />}
                {tab === "about" && <AboutStudyPanel about={b.about_study} />}
                {tab === "request" && <JsonPane obj={build?.request} label="Request" onCopy={copy} testId="sb-json-request" />}
                {tab === "result" && <JsonPane obj={build ? { ...build, build: b } : null} label="Result" onCopy={copy} testId="sb-json-result" />}
                {tab === "api" && <ApiCall build={build} />}
            </div>
        </div>
    );
}

function Progress({ build }) {
    const p = build.progress || {};
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 mb-4" data-testid="sb-detail-progress">
            <div className="flex items-center justify-between text-xs text-white/60">
                <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-[#c7a6ff]" /> Working — this view refreshes every 4 s</span>
                <span className="tabular-nums">{p.percent ?? 0}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 mt-2 overflow-hidden"><div className="h-full bg-[#7C3AED] transition-all" style={{ width: `${p.percent ?? 0}%` }} /></div>
            <Steps steps={build.steps} />
            {build.status_detail ? (
                <div className="mt-2 text-[11px] text-amber-200/90 border border-amber-400/30 bg-amber-400/[0.06] rounded px-2 py-1.5" data-testid="sb-status-detail">{build.status_detail}</div>
            ) : null}
        </div>
    );
}

function Steps({ steps }) {
    if (!steps) return null;
    return (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="sb-steps">
            {Object.entries(steps).map(([k, s]) => (
                <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 text-white/60" data-testid={`sb-step-${k}`}>
                    {k}: <span className={s?.status === "failed" ? "text-rose-300" : s?.status === "cancelled" ? "text-white/40" : ["ready", "existing", "done"].includes(s?.status) ? "text-emerald-300" : "text-sky-300"}>{s?.status || "—"}</span>
                    {s?.job_id ? <span className="text-white/30"> · job {String(s.job_id).slice(0, 8)}</span> : null}
                </span>
            ))}
        </div>
    );
}

function Overview({ build, b, discrepancies }) {
    const totals = b.totals || build?.summary?.totals || {};
    const ds = b.discrepancy_summary || build?.summary?.discrepancies || {};
    const srcs = build?.request?.sources || {};
    return (
        <div className="space-y-4" data-testid="sb-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {["protocol", "budget", "cta"].map((k) => srcs[k] && (
                    <div key={k} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5" data-testid={`sb-source-${k}`}>
                        <div className="text-white/40 uppercase tracking-wider text-[10px]">{k} · {srcs[k].mode}</div>
                        <div className="mt-1 truncate text-white/80" title={srcs[k].filename || srcs[k].id}>{srcs[k].filename || srcs[k].id}</div>
                        <div className="text-white/40 font-mono text-[10px] mt-0.5">{(build?.[`${k}_id`] || srcs[k].id || "").slice(0, 8)}</div>
                    </div>
                ))}
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5" data-testid="sb-matching">
                    <div className="text-white/40 uppercase tracking-wider text-[10px]">matching</div>
                    <div className="mt-1 text-white/80">{b.matching?.method || build?.request?.matching || "—"}{b.matching?.model ? ` · ${b.matching.model}` : ""}</div>
                    <div className="text-white/40 text-[10px] mt-0.5">{b.matching ? `${b.matching.visit_pairs} visit pairs · ${b.matching.procedure_pairs}/${b.matching.budget_lines} budget lines matched` : ""}{b.matching?.error ? ` · AI unavailable: ${b.matching.error}` : ""}</div>
                </div>
            </div>
            {b.visits?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    {Object.entries(totals).map(([k, v]) => (
                        <div key={k} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`sb-total-${k}`}>
                            <div className="text-xs text-white/40">{k.replace(/_usd$/, "").replace(/_/g, " ")}</div>
                            <div className="mt-1 text-base tabular-nums">{money(v)}</div>
                        </div>
                    ))}
                </div>
            )}
            <PatientTotalCheck t={b.per_patient_total} money={money} />
            {b.schedule && (
                <div className="text-xs text-white/60 flex flex-wrap gap-x-4 gap-y-1" data-testid="sb-schedule-facts">
                    <span>{b.schedule.visit_count} protocol visits</span>
                    <span>{(b.budget_summary?.visit_columns || []).length} budget columns</span>
                    <span>{b.schedule.procedure_count} scheduled procedures</span>
                    <span>{(b.schedule.cohorts || []).length} cohorts{b.schedule.cohort_specific_visits?.length ? ` · ${b.schedule.cohort_specific_visits.length} cohort-specific visit(s)` : ""}</span>
                    {b.budget_summary?.per_patient_total_declared_usd != null && <span>budget declares {money(b.budget_summary.per_patient_total_declared_usd)} / patient</span>}
                </div>
            )}
            {discrepancies.length > 0 ? (
                <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3" data-testid="sb-discrepancy-summary">
                    <div className="flex items-center gap-2 text-amber-300 text-sm font-medium"><AlertTriangle className="w-4 h-4" /> {ds.total ?? discrepancies.length} protocol ↔ budget disagreements</div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                        {["high", "medium", "low", "info"].map((s) => <span key={s} className={`text-[11px] px-2 py-0.5 rounded-full border ${SEV[s]}`} data-testid={`sb-sev-${s}`}>{ds[s] ?? discrepancies.filter((d) => d.severity === s).length} {s}</span>)}
                    </div>
                    <ul className="mt-2 space-y-1 text-xs text-amber-100/80">
                        {discrepancies.slice(0, 5).map((d) => <li key={d.id}>· {d.message}</li>)}
                        {discrepancies.length > 5 && <li className="text-white/40">… see the Discrepancies tab</li>}
                    </ul>
                </div>
            ) : build?.status === "ready" ? (
                <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-3 text-sm text-emerald-200 flex items-center gap-2" data-testid="sb-no-discrepancies"><CheckCircle2 className="w-4 h-4" /> Protocol and budget are fully reconciled.</div>
            ) : null}
            {Object.keys(b.payment_terms || {}).length > 0 && (
                <div className="text-xs text-white/60" data-testid="sb-payment-terms">Payment terms: {Object.entries(b.payment_terms).filter(([, v]) => v != null).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ")}{b.milestones?.length ? ` · ${b.milestones.length} milestone(s)` : ""}</div>
            )}
        </div>
    );
}

function Discrepancies({ items }) {
    const [filter, setFilter] = useState("all");
    const shown = filter === "all" ? items : items.filter((d) => d.severity === filter);
    if (!items.length) return <div className="text-sm text-white/50 p-6 text-center" data-testid="sb-discrepancies-empty">No discrepancies.</div>;
    return (
        <div data-testid="sb-discrepancies">
            <div className="flex gap-1 mb-3">
                {["all", "high", "medium", "low", "info"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`sb-disc-filter-${s}`} className={`text-[11px] px-2 py-0.5 rounded-full border ${filter === s ? "border-white/40 text-white" : "border-white/10 text-white/50"}`}>{s}</button>
                ))}
            </div>
            <table className="w-full text-xs">
                <thead className="text-white/40"><tr><th className="text-left px-2 py-1.5 font-normal">Severity</th><th className="text-left px-2 py-1.5 font-normal">Type</th><th className="text-left px-2 py-1.5 font-normal">Visit / procedure</th><th className="text-left px-2 py-1.5 font-normal">Protocol says</th><th className="text-left px-2 py-1.5 font-normal">Budget says</th><th className="text-left px-2 py-1.5 font-normal">Detail</th></tr></thead>
                <tbody>
                    {shown.map((d) => (
                        <tr key={d.id} className="border-t border-white/5 align-top" data-testid={`sb-disc-${d.id}`}>
                            <td className="px-2 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${SEV[d.severity] || SEV.info}`}>{d.severity}</span></td>
                            <td className="px-2 py-1.5 font-mono text-white/60">{d.type}</td>
                            <td className="px-2 py-1.5 text-white/80">{[d.visit, d.procedure].filter(Boolean).join(" · ")}{d.visits?.length ? <span className="text-white/40"> ({d.visits.join(", ")})</span> : null}</td>
                            <td className="px-2 py-1.5 text-white/60">{d.protocol_says || "—"}</td>
                            <td className="px-2 py-1.5 text-white/60">{d.budget_says || d.cta_says || "—"}</td>
                            <td className="px-2 py-1.5 text-white/70">{d.message}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Window + anchor summary for one visit: "+7 d from V2 · ± 2 days · days 5…9".
function TimingChip({ v, i }) {
    const w = v.window || {};
    const hasWindow = w.before_days || w.after_days || w.label;
    const anchored = v.anchor && v.anchor.relation !== "self";
    if (!hasWindow && !anchored && v.offset_days_from_anchor == null) return null;
    const off = v.offset_days_from_anchor;
    const parts = [];
    if (anchored && off != null) parts.push(`${off >= 0 ? "+" : ""}${off} d from ${v.anchor.label || v.anchor.name}${v.anchor.relation === "visit" ? ` (${v.anchor.name})` : ""}`);
    if (hasWindow) parts.push(w.label || `−${w.before_days}/+${w.after_days} d`);
    if (hasWindow && w.earliest_day != null && (w.before_days || w.after_days)) parts.push(`days ${w.earliest_day}…${w.latest_day}`);
    return (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${v.anchor?.relation === "visit" ? "border-sky-400/40 text-sky-300" : "border-white/10 text-white/50"}`} data-testid={`sb-visit-timing-${i}`} title="Offset from the anchor visit · visit window · resulting study-day range">
            {parts.join(" · ")}
        </span>
    );
}

function AnchorNote({ visits }) {
    const anchor = visits.find((v) => v.is_anchor);
    if (!anchor) return null;
    const elsewhere = visits.filter((v) => v.anchor?.relation === "visit");
    return (
        <div className="text-[11px] text-white/50 px-1" data-testid="sb-anchor-note">
            Study days are counted from <span className="text-emerald-300">{anchor.visit_number ? `V${anchor.visit_number} · ` : ""}{anchor.name}</span>
            {anchor.timing_label && anchor.timing_label !== anchor.name ? ` (${anchor.timing_label})` : ""}; windows are relative to that visit
            {elsewhere.length ? <> except <span className="text-sky-300">{elsewhere.map((v) => `${v.name} → ${v.anchor.name}`).join(", ")}</span></> : null}.
        </div>
    );
}

const FLAG_LABELS = [["eb", "EB", "Enrollment / baseline"], ["ra", "Ra", "Randomization"], ["pv", "Pv", "Priority visit (fasting / timed)"],
    ["sm", "Sm", "Send text confirmation"], ["un", "Un", "Unscheduled"], ["co", "Co", "Counted"], ["pr", "Pr", "Projected"],
    ["sf", "SF", "Can screen fail"], ["inv", "Inv", "Invoice separately"], ["ow", "0w", "0% withholding"], ["ph", "Ph", "Phone visit"], ["th", "Th", "Telehealth"]];

/** SiteCentric visit setup: type, length and the 12 per-visit flags (filled = true). */
function VisitFlags({ v, i }) {
    if (!v.flags) return null;
    return (
        <div className="flex flex-wrap items-center gap-1 px-3 pb-2.5 -mt-1 pl-9" data-testid={`sb-visit-flags-${i}`} title={v.attributes_reason || undefined}>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/15 text-white/70 capitalize" data-testid={`sb-visit-type-${i}`}>{v.visit_type}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/15 text-white/70 tabular-nums" data-testid={`sb-visit-length-${i}`}>{v.length_minutes} min</span>
            {FLAG_LABELS.map(([k, label, hint]) => (
                <span key={k} title={hint} data-testid={`sb-visit-flag-${k}-${i}`} data-on={v.flags[k] ? "true" : "false"}
                      className={`text-[9px] w-6 text-center py-0.5 rounded-full border ${v.flags[k] ? "bg-teal-500/80 border-teal-400 text-white" : "border-teal-400/30 text-teal-300/60"}`}>{label}</span>
            ))}
            {v.attributes_reason && <span className="text-[10px] text-white/35 ml-1 truncate max-w-[28rem]" data-testid={`sb-visit-reason-${i}`}>{v.attributes_reason}</span>}
            {["ow", "inv"].map((k) => {
                const ev = v.flag_evidence?.[k];
                if (!ev) return null;
                return (
                    <div key={k} className="basis-full text-[10px] text-white/60 pl-0.5" data-testid={`sb-visit-evidence-${k}-${i}`}>
                        <span className="text-teal-300">{k === "ow" ? "0w" : "Inv"} {ev.value === false ? "off" : "on"}</span>
                        <span className="text-white/45"> · {ev.source === "default" ? "default for unscheduled visits" : ev.source}{ev.passage ? ` ${ev.passage}` : ""}</span>
                        {ev.quote && <span className="italic text-white/75"> — “{ev.quote}”</span>}
                    </div>
                );
            })}
        </div>
    );
}

const GENDER_LABEL = { female: "F", male: "M", all: "All" };

/** Required (filled = required) · who it applies to · additional (budget-only) tag. */
function ProcRequirement({ p, id }) {
    if (p.required === undefined) return <span className="text-white/20">—</span>;
    return (
        <span className="inline-flex items-center gap-1" data-testid={`sb-proc-req-${id}`}>
            <span data-testid={`sb-proc-required-${id}`} data-on={p.required ? "true" : "false"} title={p.required_reason || (p.required ? "required per protocol" : "optional")}
                  className={`text-[9px] w-7 text-center py-0.5 rounded-full border ${p.required ? "bg-teal-500/80 border-teal-400 text-white" : "border-amber-400/50 text-amber-200"}`}>{p.required ? "Req" : "Opt"}</span>
            <span data-testid={`sb-proc-gender-${id}`} title={p.gender_reason || undefined}
                  className={`text-[9px] px-1.5 py-0.5 rounded-full border ${p.gender === "all" ? "border-white/15 text-white/50" : "border-fuchsia-400/50 text-fuchsia-200"}`}>{GENDER_LABEL[p.gender] || p.gender}</span>
            {p.additional_procedure && <span data-testid={`sb-proc-additional-${id}`} className="text-[9px] px-1.5 py-0.5 rounded-full border border-sky-400/40 text-sky-200" title="only in the budget — not in the protocol schedule">addl</span>}
        </span>
    );
}

const evSource = (ev) => (ev.source === "visit" ? "whole visit" : ev.source === "default" ? "default" : ev.source);

/** Procedure-level Inv / 0w chips (filled = on); the quote is in the tooltip and under the label. */
function ProcFlags({ p, id }) {
    if (!p.flags) return <span className="text-white/20">—</span>;
    return (
        <span className="inline-flex gap-1" data-testid={`sb-proc-flags-${id}`}>
            {[["inv", "Inv"], ["ow", "0w"]].map(([k, label]) => {
                const ev = p.flag_evidence?.[k];
                return (
                    <span key={k} data-testid={`sb-proc-flag-${k}-${id}`} data-on={p.flags[k] ? "true" : "false"}
                          title={ev ? `${evSource(ev)}${ev.quote ? ` — “${ev.quote}”` : ""}` : undefined}
                          className={`text-[9px] w-6 text-center py-0.5 rounded-full border ${p.flags[k] ? "bg-teal-500/80 border-teal-400 text-white" : "border-teal-400/30 text-teal-300/60"}`}>{label}</span>
                );
            })}
        </span>
    );
}

function ProcEvidence({ p, id }) {
    const keys = ["inv", "ow"].filter((k) => p.flag_evidence?.[k]?.quote);
    return keys.map((k) => (
        <div key={k} className="text-[10px] text-white/55" data-testid={`sb-proc-evidence-${k}-${id}`}>
            <span className="text-teal-300">{k === "ow" ? "0w" : "Inv"} {p.flag_evidence[k].value === false ? "off" : "on"}</span>
            <span className="text-white/45"> · {evSource(p.flag_evidence[k])}</span>
            <span className="italic text-white/70"> — “{p.flag_evidence[k].quote}”</span>
        </div>
    ));
}

function Visits({ visits, studyExtra }) {
    const [open, setOpen] = useState({});
    if (!visits.length) return <div className="text-sm text-white/50 p-6 text-center" data-testid="sb-visits-empty">No visits yet.</div>;
    const FROM = { "protocol+budget": "text-emerald-300", protocol: "text-amber-300", budget: "text-sky-300" };
    return (
        <div className="space-y-2" data-testid="sb-visits">
            <AnchorNote visits={visits} />
            {visits.map((v, i) => {
                const key = v.name || v.label || i;
                const unpaid = (v.procedures || []).filter((p) => p.matched_from === "protocol").length;
                const extra = (v.procedures || []).filter((p) => p.matched_from === "budget").length;
                return (
                    <div key={key} className="border border-white/10 rounded-lg bg-white/[0.02]" data-testid={`sb-visit-${i}`}>
                        <button onClick={() => setOpen({ ...open, [key]: !open[key] })} className="w-full flex items-center justify-between p-3 text-left" data-testid={`sb-visit-toggle-${i}`}>
                            <div className="flex items-center gap-2 text-sm">
                                {open[key] ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                                <span className="font-medium">{v.visit_number ? `V${v.visit_number} · ` : ""}{v.name || v.label}</span>
                                <span className="text-white/40 text-xs">{[v.timing_label, v.location].filter(Boolean).join(" · ")}</span>
                                {v.is_anchor && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300" data-testid={`sb-visit-anchor-${i}`}>anchor · {v.anchor?.day_offset_from_baseline === 1 ? "Day 1" : "Day 0"}</span>}
                                <TimingChip v={v} i={i} />
                                {v.path && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-teal-400/40 text-teal-200" data-testid={`sb-visit-path-${i}`} title="SiteCentric path">path: {v.path}</span>}
                                {v.cohorts?.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-violet-400/40 text-violet-300">{v.cohorts.join(", ")}</span>}
                                {v.matched_from === "rule"
                                    ? <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-teal-400/40 text-teal-300" data-testid={`sb-visit-added-${i}`}>added · invoiced separately</span>
                                    : <span className={`text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 ${v.budget_label ? "text-white/60" : "text-rose-300"}`}>{v.budget_label ? `budget: ${v.budget_label}` : "not in budget"}</span>}
                            </div>
                            <div className="text-right text-xs">
                                <div className="text-emerald-400 tabular-nums text-sm">{money(v.visit_total_usd)}</div>
                                <div className="text-white/40">{(v.procedures || []).length} procedures{unpaid ? ` · ${unpaid} unpaid` : ""}{extra ? ` · ${extra} not in protocol` : ""}</div>
                            </div>
                        </button>
                        <VisitFlags v={v} i={i} />
                        {open[key] && (
                            <table className="w-full text-xs border-t border-white/5" data-testid={`sb-visit-procedures-${i}`}>
                                <thead className="text-white/40"><tr><th className="text-left px-3 py-1.5 font-normal">Procedure (protocol)</th><th className="text-left px-3 py-1.5 font-normal">Budget line</th><th className="text-left px-3 py-1.5 font-normal">Match</th><th className="text-left px-3 py-1.5 font-normal">Req · Sex</th><th className="text-left px-3 py-1.5 font-normal">Inv · 0w</th><th className="text-left px-3 py-1.5 font-normal" title="Apply Overhead · Apply Withholding">OH · WH</th><th className="text-right px-3 py-1.5 font-normal">Amount</th></tr></thead>
                                <tbody>
                                    {(v.procedures || []).map((p, j) => (
                                        <tr key={`${p.label}-${j}`} className="border-t border-white/5">
                                            <td className="px-3 py-1.5 text-white/80">{p.label}{p.category ? <span className="text-white/30"> · {p.category}</span> : null}
                                                <ProcEvidence p={p} id={`${i}-${j}`} /><ProcBillingNote p={p} id={`${i}-${j}`} /></td>
                                            <td className="px-3 py-1.5 text-white/60">{p.budget_line || "—"}</td>
                                            <td className={`px-3 py-1.5 ${FROM[p.matched_from] || ""}`}>{p.matched_from}{p.match_method ? ` · ${p.match_method}` : ""}{p.confidence != null && p.match_method !== "exact" ? ` ${Math.round(p.confidence * 100)}%` : ""}</td>
                                            <td className="px-3 py-1.5"><ProcRequirement p={p} id={`${i}-${j}`} /></td>
                                            <td className="px-3 py-1.5"><ProcFlags p={p} id={`${i}-${j}`} /></td>
                                            <td className="px-3 py-1.5"><ProcBilling p={p} id={`${i}-${j}`} /></td>
                                            <td className="px-3 py-1.5 text-right tabular-nums">{p.marker ? <span className="text-sky-300">{p.marker}</span> : money(p.total_usd)}</td>
                                        </tr>
                                    ))}
                                    <AdditionalProcedureRows v={v} money={money} i={i} />
                                    <OverheadRow v={v} money={money} i={i} />
                                    {(v.ancillary || []).map((a, j) => (
                                        <tr key={`anc-${j}`} className="border-t border-white/5 text-white/50">
                                            <td className="px-3 py-1.5" colSpan={2}>{a.label} <span className="text-white/30">· ancillary</span></td><td className="px-3 py-1.5">budget</td><td /><td /><td /><td className="px-3 py-1.5 text-right tabular-nums">{money(a.amount_usd)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                );
            })}
            <StudyAdditionalProcedures items={studyExtra} money={money} />
        </div>
    );
}

function JsonPane({ obj, label, onCopy, testId }) {
    const text = useMemo(() => (obj ? JSON.stringify(obj, null, 2) : "—"), [obj]);
    return (
        <div className="relative" data-testid={testId}>
            <button onClick={() => onCopy(obj, label)} className="absolute top-2 right-2 text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1 bg-black/50 px-1.5 py-0.5 rounded" data-testid={`${testId}-copy`}><Copy className="w-3 h-3" /> copy</button>
            <pre className="text-[11px] bg-black/40 border border-white/10 rounded px-3 py-3 overflow-auto font-mono leading-relaxed text-white/80 whitespace-pre max-h-[60vh]">{text}</pre>
        </div>
    );
}

function ApiCall({ build }) {
    const base = resolveBackendUrl() || "https://your-aiservice-host.example";
    const srcs = build?.request?.sources || {};
    const kinds = ["protocol", "budget", "cta"].filter((k) => srcs[k]);
    const anyUpload = kinds.some((k) => srcs[k].mode === "upload");
    const site = build?.request?.site_id || "207";
    const matching = build?.request?.matching || "ai";
    const poll = `# poll until progress.terminal is true — the response carries request + full build + discrepancies\ncurl "${base}/api/v1/study-builds/${build?.id || "<build_id>"}" -H "Authorization: Bearer $CLIENT_TOKEN"`;
    let snippet;
    if (anyUpload) {
        const parts = kinds.map((k) => (srcs[k].mode === "upload" ? `  -F "${k}_file=@${srcs[k].filename}" \\` : `  -F "${k}_id=${srcs[k].id}" \\`));
        snippet = `# SiteCentric → AIService with the client token (API key) — new files (multipart)\ncurl -X POST "${base}/api/v1/study-builds/upload" \\\n  -H "Authorization: Bearer $CLIENT_TOKEN" \\\n${parts.join("\n")}\n  -F "site_id=${site}" -F "matching=${matching}"\n\n${poll}`;
    } else {
        const body = { ...Object.fromEntries(kinds.map((k) => [`${k}_id`, srcs[k].id])), site_id: site, matching };
        snippet = `# SiteCentric → AIService with the client token (API key) — documents already in the Data Lake (JSON)\ncurl -X POST "${base}/api/v1/study-builds" \\\n  -H "Authorization: Bearer $CLIENT_TOKEN" -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 2)}'\n\n${poll}`;
    }
    const copy = async () => { try { await navigator.clipboard.writeText(snippet); toast.success("curl copied"); } catch { toast.error("Copy was blocked by the browser"); } };
    return (
        <div className="relative" data-testid="sb-api-call">
            <button onClick={copy} className="absolute top-2 right-2 text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1 bg-black/50 px-1.5 py-0.5 rounded" data-testid="sb-api-call-copy"><Copy className="w-3 h-3" /> copy</button>
            <pre className="text-[11px] bg-black/40 border border-white/10 rounded px-3 py-3 overflow-auto font-mono leading-relaxed text-emerald-100 whitespace-pre">{snippet}</pre>
            <p className="text-xs text-white/40 mt-2">Same request the console just made, as SiteCentric sends it. Full request / response samples live in Capabilities → API Reference → Study build.</p>
        </div>
    );
}
